import { CastlabsPlayerLoader } from './CastlabsPlayerLoader';
import { Environment } from '../../../config/environment';
import { Log, Registry } from '@lightningjs/sdk';
import { PlayerService } from '../PlayerService';
import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';
import { AudioTrack, drmProviders, PlayerEvent, SubtitleTrack, VideoQuality } from '../PlayerModel';
import { Backend } from '../../../api/Backend';
import { IProperty, IStreamManifestResponseV3 } from '../../../api/models/AndromeTV.types';
import { BroadpeakConfiguration, DrmConfiguration, NetAttemptParameters, Rendition, Track } from './ClppModel';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// const clpp: any;
declare let clpp: any;

/**
 * Player service for Castlabs player
 * @extends {PlayerService}
 */
export class CastlabsPlayerService extends PlayerService {
  private _player: any;
  private _selectedQuality: VideoQuality | undefined;
  private _selectedSubtitle: SubtitleTrack | undefined;
  private _playerInitPromise: Promise<void> | undefined;
  private videoWidth: number | undefined;
  private videoHeight: number | undefined;

  constructor() {
    super();
  }

  /**
   * Initialize player
   * @returns {Promise<*>}
   * @private
   */
  override bootstrap(): Promise<void> {
    return CastlabsPlayerLoader.ensureResources();
  }

  /**
   * Create player container
   * @private
   */
  _ensurePlayer() {
    let playerContainer: HTMLVideoElement | null = document.body.querySelector('#castlabs-container');

    if (!playerContainer) {
      const canvasElement = document.querySelector('canvas');
      // create container
      playerContainer = document.createElement('video');
      playerContainer.id = 'castlabs-container';
      playerContainer.style.width = `${canvasElement?.width ? canvasElement.width : 1920}px`;
      playerContainer.style.height = `${canvasElement?.height ? canvasElement.height : 1080}px`;
      playerContainer.style.position = 'absolute';
      playerContainer.style.background = 'black';

      document.body.appendChild(playerContainer);
    }

    this._player = new clpp.Player(playerContainer, {
      license: Environment.Castlabs.licenseKey,
      autoplay: true,
    });

    this._player.on(clpp.events.BITRATE_CHANGED, (bitrateEvent: any) => {
      this.videoWidth = bitrateEvent.detail.width;
      this.videoHeight = bitrateEvent.detail.height;
    });

    this._player.use(clpp.dash.DashComponent);
    this._player.use(clpp.ttml.TtmlComponent);
  }

  /**
   * Configures player to play asset
   * @param {Backend} backend
   * @param {IStreamManifestResponseV2} manifest
   * @param {number} initialBitrate
   * @param {string} initialAudioLanguage
   * @param {number} startTime
   * @private
   */
  _loadAsset(
    backend: Backend,
    manifest: IStreamManifestResponseV3,
    initialBitrate: number,
    initialAudioLanguage: string,
    startTime: number | undefined
  ) {
    let drmConfig = undefined;
    if (manifest.drm) {
      drmConfig = this._createDrmConfig(backend, manifest);
    }

    const broadpeakPlugin = this._broadpeakAnalyticsConfig(backend);
    if (broadpeakPlugin && clpp.broadpeak.BroadpeakPlugin.isSdkMissing()) {
      Log.warn('Broadpeak plugin not loaded');
    }

    Registry.addEventListener(this._player, PlayerEvent.LOADEDMETADATA, () => {
      if (this.isLive && startTime) {
        this.seekAbsolute(startTime);
      }
    });

    this._player.load({
      source: [
        {
          url: manifest.url,
          drmProtected: manifest.url,
        },
      ],
      startTime: startTime,
      abr: {
        enabled: true,
        defaultBandwidthEstimate: initialBitrate,
      },
      drm: drmConfig,
      broadpeak: broadpeakPlugin,
      suggestedPresentationDelay: 10, // TODO
      manifest: {
        attemptParameters: this._networkAttemptParameters(),
      },
      streaming: {
        attemptParameters: this._networkAttemptParameters(),
      },
      preferredAudioLanguage: initialAudioLanguage,
    });

    this._selectedQuality = undefined;
    this._selectedSubtitle = undefined;
  }

  override start(
    backend: Backend,
    manifest: IStreamManifestResponseV3,
    initialBitrate: number,
    initialAudioLanguage: string,
    startTime: number | undefined
  ) {
    Log.info('init player', manifest);

    return new Promise<void>(resolve => {
      Log.info('ensure player');
      this._ensurePlayer();
      Log.info('loaded playing video', manifest);
      this._loadAsset(backend, manifest, initialBitrate, initialAudioLanguage, startTime);
      resolve();
    });
  }

  /**
   * Closes player
   */
  override close() {
    if (this._player) {
      this._player.release();
    }
  }

  /**
   * @param {Backend} backend
   * @param {IStreamManifestResponseV2} manifest
   * @returns {DrmConfiguration}
   * @private
   */
  _createDrmConfig(backend: Backend, manifest: IStreamManifestResponseV3): DrmConfiguration | undefined {
    if (manifest.drmSessionId && manifest.assetId) {
      const drmConfig: DrmConfiguration = {
        env: Environment.Castlabs.drmEnvironment,
        preferredDrmSystem: backend.playerController.preferredDrmProvider,
        customData: {
          userId: backend.sessionInfo?.customer.id,
          sessionId: manifest.drmSessionId,
          merchant: Environment.Castlabs.drmMerchant,
          assetId: manifest.assetId,
          authToken: undefined,
        },
      };

      if (manifest.drmAuthToken) {
        drmConfig.customData.authToken = manifest.drmAuthToken;
      }

      Log.info('drmConfig', drmConfig);
      return drmConfig;
    }
    return undefined;
  }

  /**
   * Adds eventListener to player
   */
  override on(event: string, callback: (event: any) => any) {
    Registry.addEventListener(this._player, event, callback);
  }

  /**
   * Removes all eventListeners on player
   */
  override removeEventListeners() {
    if (this._player) Registry.removeEventListeners(this._player);
  }

  /**
   * @param {Backend} backend
   * @returns {BroadpeakConfiguration}
   * @private
   */
  _broadpeakAnalyticsConfig(backend: Backend): BroadpeakConfiguration | undefined {
    const enabled = backend.globalSettings.boolValue(GlobalSettingsKeys.broadpeakAnalyticsEnabled) || false;
    if (enabled) {
      const domain = backend.globalSettings.stringValue(GlobalSettingsKeys.broadpeakAnalyticsDomain);
      const analytics = backend.globalSettings.stringValue(GlobalSettingsKeys.broadpeakAnalyticsServer);
      if (domain && analytics) {
        return {
          broadpeakDomainNames: domain,
          analyticsAddress: analytics,
        };
      } else if (domain) {
        return {
          analyticsAddress: '',
          broadpeakDomainNames: domain,
        };
      }
      return undefined;
    } else {
      return undefined;
    }
  }

  /**
   * @returns {NetAttemptParameters}
   * @private
   */
  _networkAttemptParameters(): NetAttemptParameters {
    return {
      maxAttempts: 5,
      baseDelay: 500,
      backoffFactor: 2,
      fuzzFactor: 0.5,
      timeout: 8000,
    };
  }

  override get absolutePosition() {
    return Math.round(this._player.getPosition());
  }

  override get relativePosition() {
    return Math.round(this._player.getPosition() - this._player.getSeekRange().start);
  }

  override get duration() {
    const duration = this._player.getDuration();
    return duration !== Infinity ? Math.round(duration) : Infinity;
  }

  override seekAbsolute(time: number | undefined) {
    if (this.isLive) {
      time += this._player.getSeekRange().start;
    }
    this._player.seek(time);
  }

  override get seekRange() {
    const range = this._player.getSeekRange();
    range.end = range.end - range.start;
    range.start = 0;
    return range;
  }

  override pause() {
    this._player.pause();
  }

  override play() {
    this._player.play();
  }

  override configurePlaySpeed(speed: number) {
    if (speed !== 1) {
      this._player.setMuted(true);
    } else {
      this._player.setMuted(false);
    }
    this._player.setPlaybackRate(speed);
  }

  override get videoQualities() {
    const renditions = this._listVideoRenditions();

    const renditionsMap = new Map();

    renditions.forEach(r => {
      const height = r.height;
      const frameRate = r.track.frameRate;
      const key = `${height}@${frameRate}`;
      renditionsMap.set(key, {
        height: r.height,
        id: r.id,
        framerate: frameRate,
        active: this._selectedQuality && this._selectedQuality.id === r.id,
      });
    });

    return Array.from(renditionsMap.values());
  }

  override get selectedVideoQuality() {
    return this._selectedQuality;
  }

  /**
   * @returns {Rendition[]}
   * @private
   */
  _listVideoRenditions(): Rendition[] {
    const renditions: Rendition[] = [];

    let trackList = this._player
      .getTrackManager()
      .getVideoTracks()
      .filter((t: Track) => t.roles?.includes('main'));

    if (trackList.length === 0 && this._player.getTrackManager().getVideoTracks().length >= 1) {
      trackList = [this._player.getTrackManager().getVideoTracks()[0]];
    }

    trackList.forEach((track: Track) => {
      renditions.push(...track.renditions);
    });

    return renditions;
  }

  override setVideoQuality(quality: VideoQuality | undefined) {
    this._selectedQuality = quality;
    this._player
      .getTrackManager()
      .setVideoRendition(quality ? this._listVideoRenditions().find(r => quality && r.id === quality.id) : null, true);
  }

  /**
   * @returns {Track[]}
   * @private
   */
  _listMainAudioTracks(): Track[] {
    return this._player
      .getTrackManager()
      .getAudioTracks()
      .filter((track: Track) => track.roles?.includes('main') || (track.roles?.length === 0 && track.kind === 'main'));
  }

  override get audioTracks(): AudioTrack[] {
    const selectedTrack = this.selectedAudioTrack;
    return this._listMainAudioTracks().map(t => {
      const audioTrack: AudioTrack = {
        id: t.id,
        channelsCount: t.channelsCount,
        label: t.label,
        language: t.language,
        active: (selectedTrack && selectedTrack.id === t.id) || false,
      };
      return audioTrack;
    });
  }

  override get selectedAudioTrack(): AudioTrack | undefined {
    const track = this._player.getTrackManager().getAudioTrack();
    return track
      ? {
          id: track.id,
          channelsCount: track.channelsCount,
          label: track.label,
          language: track.language,
          active: true,
        }
      : undefined;
  }

  override setAudioTrack(track: AudioTrack) {
    this._player.getTrackManager().setAudioTrack(this._listMainAudioTracks().find(t => t.id === track.id));
  }

  override get subtitleTracks() {
    return this._player
      .getTrackManager()
      .getTextTracks()
      .map((t: any) => ({
        id: t.id,
        language: t.language,
        label: t.label,
        representationId: t.renditions && t.renditions.length > 0 ? t.renditions[0].originalId : undefined,
        active: this._selectedSubtitle && this._selectedSubtitle.id === t.id,
      }));
  }

  override get selectedSubtitleTrack() {
    return this._selectedSubtitle;
  }

  override setSubtitleTrack(track: SubtitleTrack) {
    this._selectedSubtitle = track;
    this._player.getTrackManager().setTextTrack(
      track
        ? this._player
            .getTrackManager()
            .getTextTracks()
            .find((t: any) => t.id === track.id)
        : null
    );
  }

  override get isLive() {
    return this._player.isLive();
  }

  override get playerReady() {
    return !!this._player;
  }

  override DRMAnalyticsProperties(): IProperty[] {
    const drm = this._player?.getDrmInfo();
    if (!drm) {
      return [];
    }
    const drmProvider = this._clppKeySystemToDrmProvider(drm.keySystem);
    let securityLevel;
    if (drmProvider === drmProviders.WIDEVINE) {
      securityLevel = this._getWideVineLevel(drm.videoRobustness);
    }
    const params = {
      provider: drmProvider,
      outputHeight: this.videoHeight,
      outputWidth: this.videoWidth,
      supportsHdr10: screen.colorDepth > 24,
      securityLevel,
    };
    const map = new Map(Object.entries(params));

    return Object.keys(params).map(key => {
      const prop: IProperty = {
        name: `drm.${key}`,
        value: map.get(key) as string,
      };
      return prop;
    });
  }

  _clppKeySystemToDrmProvider(keySystem: string) {
    switch (keySystem) {
      case clpp.drm.KeySystem.NONE:
        return drmProviders.OTHER; // TODO: check this, NONE in castlabs in empty/unspecified
      case clpp.drm.KeySystem.CLEAR_KEY:
        return drmProviders.NONE;
      case clpp.drm.KeySystem.PLAYREADY:
        return drmProviders.PLAYREADY;
      case clpp.drm.KeySystem.FAIRPLAY:
        return drmProviders.FAIRPLAY;
      case clpp.drm.KeySystem.WIDEVINE:
        return drmProviders.WIDEVINE;
      case clpp.drm.KeySystem.PRIMETIME:
      default:
        return drmProviders.OTHER;
    }
  }

  _getWideVineLevel(videoRobustness: string[] | string) {
    if (typeof videoRobustness === 'string') {
      videoRobustness = [videoRobustness];
    }
    const levels = videoRobustness.map((robustness: string) => {
      switch (robustness) {
        case wideVineVideoRobustness.HW_SECURE_ALL:
        case wideVineVideoRobustness.HW_SECURE_DECODE:
          return 'L1';
        case wideVineVideoRobustness.HW_SECURE_CRYPTO:
          return 'L2';
        case wideVineVideoRobustness.SW_SECURE_DECODE:
        case wideVineVideoRobustness.SW_SECURE_CRYPTO:
          return 'L3';
        default:
          return undefined;
      }
    });
    return levels.sort()[0];
  }

  override cleanup() {
    super.cleanup();
    const playerContainer = document.body.querySelector('#castlabs-container');
    if (playerContainer) playerContainer.remove();
    const clppContainers = document.body.querySelectorAll('.clpp-container');
    if (clppContainers) clppContainers.forEach(e => e.remove());
    CastlabsPlayerLoader.cleanup();
  }
}

const wideVineVideoRobustness = {
  SW_SECURE_CRYPTO: 'SW_SECURE_CRYPTO', //	EME Level 1, Widevine Level 3
  SW_SECURE_DECODE: 'SW_SECURE_DECODE', //	EME Level 2, Widevine Level 3
  HW_SECURE_CRYPTO: 'HW_SECURE_CRYPTO', //	EME Level 3, Widevine Level 2
  HW_SECURE_DECODE: 'HW_SECURE_DECODE', //	EME Level 4, Widevine Level 1
  HW_SECURE_ALL: 'HW_SECURE_ALL', //	EME Level 5, Widevine Level 1
};
